import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import {
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    IconButton,
    InputAdornment,
    Tooltip,
    CircularProgress,
    Divider
} from "@material-ui/core";
import {
    FileCopy as CopyIcon,
    Refresh as RefreshIcon,
    Visibility,
    VisibilityOff
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import useModules from "../../hooks/useModules";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(2),
    },

    paper: {
        padding: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        marginBottom: 12,
    },

    tokenPaper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
    },

    settingOption: {
        marginLeft: "auto",
    },
    margin: {
        margin: theme.spacing(1),
    },

    color: {
        color: "#0241AD"
    },

    text: {
        marginLeft: "42px",
        fontSize: "15px",
    },

    textP: {
        marginLeft: "42px",
        fontSize: "15px",
    },

}));

const Api = () => {
    const classes = useStyles();
    const [apiToken, setApiToken] = useState("");
    const [showToken, setShowToken] = useState(false);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const { isModuleEnabled } = useModules();
    
    // Pegar o domínio base da API dinamicamente
    const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    useEffect(() => {
        loadApiToken();
    }, []);

    const loadApiToken = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/api-token");
            setApiToken(data.token || "");
        } catch (error) {
            console.error("Erro ao carregar token:", error);
            toast.error("Erro ao carregar Token API");
        } finally {
            setLoading(false);
        }
    };

    const handleCopyToken = () => {
        navigator.clipboard.writeText(apiToken);
        toast.success("Token copiado para área de transferência!");
    };

    const handleGenerateNewToken = async () => {
        if (!window.confirm(
            "⚠️ ATENÇÃO: Gerar um novo token irá INVALIDAR o token atual!\n\n" +
            "Todas as integrações que usam o token antigo deixarão de funcionar.\n\n" +
            "Deseja realmente continuar?"
        )) {
            return;
        }

        try {
            setGenerating(true);
            const { data } = await api.post("/api-token/generate");
            setApiToken(data.token);
            toast.success("Novo token gerado com sucesso!");
        } catch (error) {
            console.error("Erro ao gerar token:", error);
            toast.error("Erro ao gerar novo token");
        } finally {
            setGenerating(false);
        }
    };

    if (!isModuleEnabled("moduleApi")) {
        return (
            <div className={classes.root}>
                <Container>
                    <Paper style={{ padding: 32, textAlign: "center" }}>
                        <Typography variant="h6" gutterBottom>
                            Módulo API não configurado
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Acesse Painel de Controle para ativar o módulo de API
                        </Typography>
                    </Paper>
                </Container>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <Container>
                {/* Seção Token API */}
                <Paper className={classes.tokenPaper}>
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
                        🔑 Token API
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: 16, opacity: 0.9 }}>
                        Use este token para autenticar suas requisições à API
                    </Typography>
                    
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress style={{ color: "white" }} />
                        </Box>
                    ) : (
                        <>
                            <TextField
                                fullWidth
                                label="Seu Token API"
                                type={showToken ? "text" : "password"}
                                value={apiToken}
                                variant="outlined"
                                InputProps={{
                                    readOnly: true,
                                    style: { backgroundColor: "rgba(255,255,255,0.1)", color: "white" },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowToken(!showToken)}
                                                edge="end"
                                                style={{ color: "white" }}
                                            >
                                                {showToken ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                InputLabelProps={{
                                    style: { color: "rgba(255,255,255,0.7)" }
                                }}
                            />
                            
                            <Box mt={2} display="flex" gap={2}>
                                <Tooltip title="Copiar token">
                                    <Button
                                        variant="contained"
                                        color="default"
                                        startIcon={<CopyIcon />}
                                        onClick={handleCopyToken}
                                    >
                                        Copiar Token
                                    </Button>
                                </Tooltip>
                                
                                <Tooltip title="Gerar novo token (invalida o atual)">
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={generating ? <CircularProgress size={20} /> : <RefreshIcon />}
                                        onClick={handleGenerateNewToken}
                                        disabled={generating}
                                    >
                                        {generating ? "Gerando..." : "Gerar Novo Token"}
                                    </Button>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                </Paper>

                <Divider style={{ margin: "24px 0" }} />

                <h2>Documentação para envio de mensagens</h2>

                <h2 className={classes.color}>Métodos de Envio</h2>

                <p className={classes.text}>1. Mensagens de Texto</p>
                <p className={classes.text}>2. Mensagens de Mídia</p>

                <h2 className={classes.color}>Instruções</h2>
                <p style={{ fontSize: "15px" }}><b>Observações Importantes</b></p>
                <ul style={{ fontSize: "15px" }}>
                    <li>O token da API está disponível acima. Copie-o e use no header Authorization de suas requisições.</li>
                    <li>O número para envio não deve ter máscara ou caracteres especiais e deve ser composto por:</li>
                    <br/>
                    <ol>
                        <ul>
                            <li>Código do país - Ex: 55 (Brasil)</li>
                            <li>DDD</li>
                            <li>Número</li>
                        </ul>
                    </ol>
                </ul>
                <h2 className={classes.color}>1. Mensagens de Texto</h2>
                <p style={{ fontSize: "15px" }}>Seguem abaixo lista de informações necessárias para envio das mensagens de texto:</p>
                <p className={classes.textP}><b>URL: </b>{apiBaseUrl}/api/messages/send</p>
                <p className={classes.textP}><b>Método: </b>POST</p>
                <p className={classes.textP}><b>Headers: </b>Authorization: Bearer (token) e Content-Type application/json</p>
                <p className={classes.textP}><b>Body: </b>"number": "5599999999999", "body": "Enviado via api"</p>
                <h2 className={classes.color}>2. Mensagens de Mídia</h2>
                <p style={{ fontSize: "15px" }}>Seguem abaixo lista de informações necessárias para envio de mídias:</p>
                <p className={classes.textP}><b>URL: </b>{apiBaseUrl}/api/messages/send</p>
                <p className={classes.textP}><b>Método: </b>POST</p>
                <p className={classes.textP}><b>Headers: </b>Authorization: Bearer (token) e Content-Type multipart/form-data</p>
                <p className={classes.textP}><b>Body: </b>"number": "5599999999999", "medias": "aqui vai sua mídia", "body": "Enviado via api"</p>
            </Container>
        </div>
    );
};

export default Api;
